import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";

export const formatRemaining = (ms) => {
  if (ms <= 0 || Number.isNaN(ms)) return null;
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  if (days >= 2) return `${days}d ${hours}h ${mins}m`;
  if (days === 1) return `1d ${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  return `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
};

const AnnouncementBar = () => {
  const [announcement, setAnnouncement] = useState(null);
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    api
      .get("/settings/announcement")
      .then((r) => {
        const a = r.data;
        if (a?.enabled && a?.text) {
          const key = `aarmira_announcement_${btoa(
            unescape(encodeURIComponent(a.text + (a.deadline || "")))
          ).slice(0, 24)}`;
          if (!localStorage.getItem(key)) {
            setAnnouncement({ ...a, key });
          }
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!announcement?.deadline) return undefined;
    const target = new Date(announcement.deadline).getTime();
    if (Number.isNaN(target)) return undefined;
    const tick = () => setRemaining(formatRemaining(target - Date.now()));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [announcement]);

  if (!announcement) return null;

  const dismiss = () => {
    localStorage.setItem(announcement.key, "1");
    setAnnouncement(null);
  };

  return (
    <div
      data-testid="announcement-banner"
      className="relative z-[55] bg-gold px-12 py-2.5"
    >
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5">
        <span className="h-1 w-1 rotate-45 bg-espresso" />
        <p
          data-testid="announcement-banner-text"
          className="text-center font-mono text-[10px] uppercase tracking-[0.25em] text-espresso"
        >
          {announcement.text}
        </p>
        {remaining && (
          <span
            data-testid="announcement-countdown"
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-espresso/70"
          >
            · Ends in {remaining}
          </span>
        )}
        <span className="h-1 w-1 rotate-45 bg-espresso" />
      </div>
      <button
        data-testid="announcement-banner-dismiss"
        onClick={dismiss}
        className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-espresso/70 transition-colors hover:bg-espresso/10"
        aria-label="Dismiss announcement"
      >
        <X size={13} />
      </button>
    </div>
  );
};

export default AnnouncementBar;
